import type { ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Mars, Venus, CircleDashed, ShieldQuestion } from "lucide-react";
import { cn } from "@/lib/utils";
import { useProfile } from "../hooks/use-profile";
import { GENDER_OPTIONS, type GenderOption } from "../utils/gender";

const GENDER_ICONS: Record<
  GenderOption,
  ComponentType<{ className?: string }>
> = {
  male: Mars,
  female: Venus,
  other: CircleDashed,
  prefer_not_to_say: ShieldQuestion,
};

/**
 * Gender Section
 * Card-style gender selection for persona building
 */
export const GenderSection: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useProfile();

  return (
    <section className="flex flex-col gap-4 md:gap-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-text-primary dark:text-gray-100 leading-6">
          {t("settings.profile.gender")}
        </h3>
        <p className="text-sm text-text-tertiary dark:text-gray-400 leading-5 max-w-2xl">
          {t("settings.profile.genderDescription")}
        </p>
      </div>

      <div
        className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3"
        role="radiogroup"
        aria-label={t("settings.profile.gender")}
      >
        {GENDER_OPTIONS.map((option) => {
          const Icon = GENDER_ICONS[option];
          const selected = formData.gender === option;

          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => updateFormData("gender", option)}
              className={cn(
                "group relative flex flex-col items-start gap-2 sm:gap-3 rounded-xl sm:rounded-2xl border p-3 sm:p-4 text-left transition-all duration-200 min-h-[5.5rem] sm:min-h-0",
                selected
                  ? "border-text-brand dark:border-indigo-400 ring-2 ring-text-brand/30 dark:ring-indigo-400/50 bg-indigo-50/50 dark:bg-indigo-500/15 shadow-sm"
                  : "bg-white dark:bg-gray-800 border-border-secondary dark:border-gray-600 hover:border-text-brand/50 dark:hover:border-indigo-400/60 hover:bg-gray-50 dark:hover:bg-gray-700"
              )}
            >
              <div
                className={cn(
                  "flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-lg sm:rounded-xl transition-colors shrink-0",
                  selected
                    ? "bg-text-brand dark:bg-indigo-500 text-white"
                    : "bg-indigo-50 dark:bg-indigo-500/15 text-text-brand dark:text-indigo-400"
                )}
              >
                <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>

              <div className="flex flex-col gap-0.5 pr-4">
                <span className="text-xs sm:text-sm font-semibold text-text-primary dark:text-gray-100 leading-tight">
                  {t(`settings.profile.genderOptions.${option}`)}
                </span>
                <span className="hidden sm:block text-xs text-text-tertiary dark:text-gray-400 leading-4">
                  {t(`settings.profile.genderHints.${option}`)}
                </span>
              </div>

              <span
                className={cn(
                  "absolute right-2.5 top-2.5 sm:right-3 sm:top-3 h-2 w-2 sm:h-2.5 sm:w-2.5 rounded-full transition-colors",
                  selected
                    ? "bg-text-brand dark:bg-indigo-400"
                    : "bg-transparent border border-border-primary dark:border-gray-500"
                )}
              />
            </button>
          );
        })}
      </div>
    </section>
  );
};
